import { Inject, Injectable, Optional, signal } from '@angular/core';
import { CS_CHAT_CONFIG, CsChatConfig, CsGlossarySource, CsGlossaryTerm } from '@cs/components/chat';

/**
 * Loads the plain-language glossary from the MCP server (`GET …/api/glossary`) and exposes it as a
 * reactive {@link CsGlossarySource} for the shared chat components — they use it to decorate the
 * first mention of each term with a tooltip and to build the "Terms in this answer" panel.
 *
 * The PM-app counterpart of the standalone chat's GlossaryService: same contract, but the URL comes
 * from CS_CHAT_CONFIG's `apiUrl` (a same-origin path such as `/ai/api/chat`) rather than a
 * switchable remote instance, so the glossary is simply that path's sibling — `/ai/api/glossary`.
 *
 * Anonymous reference content, so no bearer token is needed (unlike {@link PmChatService}); it is
 * the same endpoint the standalone app reads without auth. Fails soft: any error leaves the term
 * list empty and the chat renders undecorated, exactly as it does when no glossary is configured.
 */
@Injectable({ providedIn: 'root' })
export class PmGlossaryService implements CsGlossarySource {
	private readonly _terms = signal<CsGlossaryTerm[]>([]);
	/** Reactive glossary terms (empty until loaded / when the instance configures none). */
	readonly terms = this._terms.asReadonly();

	private readonly glossaryUrl: string;
	private loaded = false;

	constructor(@Optional() @Inject(CS_CHAT_CONFIG) config: CsChatConfig) {
		const apiUrl = (config && config.apiUrl) || '/ai/api/chat';
		// Sibling of the chat base: '/ai/api/chat' -> '/ai/api/glossary'.
		this.glossaryUrl = apiUrl.replace(/\/chat\/?$/, '/glossary');

		// Load on construction rather than making each host call load(). The singleton is created the
		// first time a chat component injects CS_CHAT_GLOSSARY, i.e. exactly when the terms are about
		// to be needed — and there is no per-host step left to forget. (The standalone app's service
		// does need an explicit load()/reset() because its base URL changes when you switch servers;
		// here the URL is fixed at construction.) `terms` is a signal, so the UI updates on arrival.
		void this.load();
	}

	/** Fetches the glossary once; repeat calls are a no-op. */
	async load(): Promise<void> {
		if (this.loaded) { return; }
		this.loaded = true;
		try {
			const resp = await fetch(this.glossaryUrl);
			if (!resp.ok) { this._terms.set([]); return; }
			const data = await resp.json() as CsGlossaryTerm[];
			this._terms.set(Array.isArray(data) ? data : []);
		} catch {
			this._terms.set([]); // fail soft — chat renders without glossary decorations
		}
	}
}
